import { useRef, useState } from "react";
import api, { formatApiError, userPhoto } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Camera, Loader2, Trash2 } from "lucide-react";

// Any signed-in person — students included — can set their own profile photo here.
export const ProfilePhotoButton = () => {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ver, setVer] = useState(0);
  const fileRef = useRef(null);

  const src = user?.photo_path ? `${userPhoto(user.id)}&v=${ver}` : null;

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/users/me/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refresh();
      setVer((v) => v + 1);
      toast.success("Profile photo updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); e.target.value = ""; }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/users/me/photo");
      await refresh();
      setVer((v) => v + 1);
      toast.success("Photo removed");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <button onClick={() => setOpen(true)} title="Your profile photo" data-testid="profile-photo-btn"
        className="relative group rounded-full focus:outline-none focus:ring-2 focus:ring-cc-blue/40">
        <Avatar src={src} name={user?.name} size={36} testId="profile-photo-avatar" />
        <span className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-white border border-zinc-200 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <Camera className="w-2.5 h-2.5 text-zinc-500" />
        </span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="profile-photo-dialog">
          <DialogHeader><DialogTitle>Your profile photo</DialogTitle></DialogHeader>
          <div className="flex flex-col items-center gap-4 py-2">
            <Avatar src={src} name={user?.name} size={112} testId="profile-photo-preview" />
            <p className="text-sm text-zinc-500 text-center">
              This is the photo people see next to your name across Campus Connections. JPG, PNG or WebP, under 5 MB.
            </p>
            <input type="file" accept="image/*" className="hidden" ref={fileRef} onChange={upload} data-testid="profile-photo-input" />
            <div className="flex items-center gap-2">
              <Button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="profile-photo-upload">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4 mr-1.5" />}
                {user?.photo_path ? "Change photo" : "Add photo"}
              </Button>
              {user?.photo_path && (
                <Button variant="outline" className="text-red-600" onClick={remove} disabled={busy} data-testid="profile-photo-remove">
                  <Trash2 className="w-4 h-4 mr-1.5" />Remove
                </Button>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="profile-photo-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
